import { useEffect, useState } from 'react';
import type { Session } from '@supabase/supabase-js';
import { Dashboard } from './components/Dashboard';
import { EditorialWorkspace } from './components/EditorialWorkspace';
import { MediaWorkspace } from './components/MediaWorkspace';
import { SocialWorkspace } from './components/SocialWorkspace';
import { IntelligenceWorkspace } from './components/IntelligenceWorkspace';
import { Login } from './components/Login';
import type { EditorialItem } from './lib/api';
import { getValidatedSession, legacyLinkError } from './lib/email-auth';
import { isConfigured, supabase } from './lib/supabase';

export function App() {
  const [workspace, setWorkspace] = useState<
    'dashboard' | 'editorial' | 'media' | 'social' | 'intelligence'
  >(
    new URLSearchParams(window.location.search).get('social') === 'connected'
      ? 'social'
      : 'dashboard',
  );
  const [selectedMediaItem, setSelectedMediaItem] = useState<Pick<
    EditorialItem,
    'id' | 'title'
  > | null>(null);
  const [mediaReturn, setMediaReturn] = useState<'dashboard' | 'editorial'>(
    'dashboard',
  );
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(isConfigured);
  const [legacyError] = useState<string | null>(() =>
    legacyLinkError(window.location.hash),
  );
  const [authError, setAuthError] = useState<string | null>(legacyError);
  useEffect(() => {
    if (!supabase) return;
    let active = true;
    let version = 0;
    let clearingLegacy = Boolean(legacyError);
    async function validate() {
      const current = ++version;
      setLoading(true);
      try {
        const valid = await getValidatedSession(supabase!.auth);
        if (!active || current !== version) return;
        setSession(valid);
        if (valid) setAuthError(null);
      } catch {
        if (!active || current !== version) return;
        setSession(null);
        setAuthError(
          'Não foi possível validar sua sessão. Confira a conexão e solicite um novo código.',
        );
      } finally {
        if (active && current === version) setLoading(false);
      }
    }
    const { data } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT') {
        version++;
        setSession(null);
        setLoading(false);
        clearingLegacy = false;
      } else if (clearingLegacy) {
        return;
      } else if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        version++;
        setSession(null);
        setLoading(true);
        setTimeout(() => {
          if (active) void validate();
        }, 0);
      }
    });
    if (legacyError) {
      window.history.replaceState(
        null,
        '',
        window.location.pathname + window.location.search,
      );
      setSession(null);
      setLoading(true);
      void supabase.auth.signOut({ scope: 'local' }).then(
        () => {
          if (active) {
            clearingLegacy = false;
            setLoading(false);
          }
        },
        () => {
          if (active) {
            clearingLegacy = false;
            setLoading(false);
          }
        },
      );
    } else {
      void validate();
    }
    return () => {
      active = false;
      version++;
      data.subscription.unsubscribe();
    };
  }, [legacyError]);
  if (loading)
    return (
      <div className="center-state">
        <span className="spinner" />
        <p>Validando sessão…</p>
      </div>
    );
  if (!session)
    return <Login key={authError ?? 'login'} initialError={authError} />;
  if (workspace === 'media')
    return (
      <MediaWorkspace
        session={session}
        initialItem={selectedMediaItem}
        onBack={() => setWorkspace(mediaReturn)}
      />
    );
  if (workspace === 'social')
    return (
      <SocialWorkspace
        session={session}
        onBack={() => setWorkspace('dashboard')}
      />
    );
  if (workspace === 'intelligence')
    return (
      <IntelligenceWorkspace
        session={session}
        onBack={() => setWorkspace('dashboard')}
      />
    );
  return workspace === 'editorial' ? (
    <EditorialWorkspace
      session={session}
      onBack={() => setWorkspace('dashboard')}
      onOpenMedia={(item) => {
        setSelectedMediaItem(item);
        setMediaReturn('editorial');
        setWorkspace('media');
      }}
    />
  ) : (
    <Dashboard
      session={session}
      onOpenEditorial={() => setWorkspace('editorial')}
      onOpenMedia={() => {
        setSelectedMediaItem(null);
        setMediaReturn('dashboard');
        setWorkspace('media');
      }}
      onOpenSocial={() => setWorkspace('social')}
      onOpenIntelligence={() => setWorkspace('intelligence')}
    />
  );
}
